import { IRelateMasterDataFormValues } from '@Router/GoalAndOperationalPlans/Containers/GoalAndOperationalPlansFormProvider/types';
import { IInfoCategoryMasterData } from '@Domains/MasterData';
import {
  CATEGORY_GROUP,
  INFO_CATEGORY_TARGET_RELATED,
  OTHER_TARGETS_ID,
} from '@Domains/TargetRelated';
import { arrange, asc, groupBy, tidy } from '@tidyjs/tidy';
import { RelateTargetsWithCategory } from '../TargetDetails';
import { checkMessageEn } from '@Utils/validation';

export type RequestRelateMasterDataFormValues = IRelateMasterDataFormValues & {
  infoCategory: string;
};

export type ExtendedRelateMasterDataFormValues = IRelateMasterDataFormValues & {
  isChecked?: boolean;
};

export type RelateTargetFormValues = {
  relateGoals: ExtendedRelateMasterDataFormValues[];
};

const mappingTargetLabel = (group: INFO_CATEGORY_TARGET_RELATED) => {
  switch (group) {
    case INFO_CATEGORY_TARGET_RELATED.GOAL_BUSINESS_PLAN:
      return 'เป้าหมายและแผนการขยายธุรกิจ';
    case INFO_CATEGORY_TARGET_RELATED.GOAL_COST_PLAN:
      return 'เป้าหมายและแผนการจัดการต้นทุน';
    case INFO_CATEGORY_TARGET_RELATED.GOAL_INVESTMENT_PLAN:
      return 'เป้าหมายและแผนการลงทุน';
    case INFO_CATEGORY_TARGET_RELATED.GOAL_DEBT_PLAN:
      return 'เป้าหมายและแผนการจัดการหนี้';
    default:
      return;
  }
};

export const mapGoalTopics = (
  goals: IInfoCategoryMasterData[] = []
): RequestRelateMasterDataFormValues[] => {
  return tidy(
    goals,
    arrange([asc('sequence')]),
    groupBy('infoCategory', (group) =>
      group.map((goal) => ({
        infoCategoryMasterDataId: goal.id,
        nameTh: goal.nameTh,
        nameEn: goal.nameEn,
        infoCategory: CATEGORY_GROUP[goal.infoCategory],
      }))
    )
  ).flat();
};

export const mapInitialValue = (
  relateGoalList: IRelateMasterDataFormValues[],
  goalTopics: RequestRelateMasterDataFormValues[]
) => {
  const relateGoals = goalTopics?.map((goalTopic) => {
    const existingGoal = relateGoalList?.find(
      (goal) =>
        goal.infoCategoryMasterDataId === goalTopic.infoCategoryMasterDataId
    );

    return {
      infoCategoryMasterDataId: goalTopic.infoCategoryMasterDataId,
      nameTh: existingGoal?.nameTh || undefined,
      nameEn: existingGoal?.nameEn || undefined,
      isChecked: existingGoal ? true : false,
    };
  });

  return { relateGoals };
};

export const mapRelateDataValues = (
  values: ExtendedRelateMasterDataFormValues[]
): IRelateMasterDataFormValues[] => {
  return values
    .filter((value) => value.isChecked)
    .map((value) => ({
      infoCategoryMasterDataId: value.infoCategoryMasterDataId,
      nameTh: value.nameTh,
      nameEn: value.nameEn,
    }));
};

const TARGET_ORDER: Record<INFO_CATEGORY_TARGET_RELATED, number> = {
  [INFO_CATEGORY_TARGET_RELATED.GOAL_BUSINESS_PLAN]: 1,
  [INFO_CATEGORY_TARGET_RELATED.GOAL_COST_PLAN]: 2,
  [INFO_CATEGORY_TARGET_RELATED.GOAL_INVESTMENT_PLAN]: 3,
  [INFO_CATEGORY_TARGET_RELATED.GOAL_DEBT_PLAN]: 4,
};

export const mapTargetByGroupsToUIDisplay = (
  targets: RelateTargetsWithCategory[]
) => {
  const targetGroups = tidy(
    targets,
    arrange([
      asc((t) => TARGET_ORDER[t.infoCategory as INFO_CATEGORY_TARGET_RELATED]),
    ]),
    groupBy('infoCategory', (group) => [
      {
        value: group[0].infoCategory,
        label: mappingTargetLabel(
          group[0].infoCategory as INFO_CATEGORY_TARGET_RELATED
        ),
        data: [...group],
      },
    ])
  );

  return targetGroups;
};

export const targetDetailsMapping = (
  selectedTargets: IRelateMasterDataFormValues[],
  data?: IInfoCategoryMasterData[]
) => {
  return (
    selectedTargets?.map((target: IRelateMasterDataFormValues) => {
      const targetId = target.infoCategoryMasterDataId;
      const masterData = data?.find((master) => master.id === targetId);

      const isOther = Object.values(OTHER_TARGETS_ID).includes(targetId);

      return {
        sequence: masterData?.sequence,
        infoCategory: masterData?.infoCategory,
        infoCategoryMasterDataId: masterData?.id,
        nameTh: isOther ? target.nameTh : masterData?.nameTh,
        nameEn: isOther ? target.nameEn : masterData?.nameEn,
      } as RelateTargetsWithCategory;
    }) ?? []
  );
};

export const checkValidData = (
  currentRelateGoals: ExtendedRelateMasterDataFormValues[]
) => {
  const otherTargets = Object.values(OTHER_TARGETS_ID);

  const checkedValues = currentRelateGoals.filter((value) => value.isChecked);

  const allOtherValid = checkedValues.every((value) => {
    if (otherTargets.includes(value.infoCategoryMasterDataId)) {
      const hasNameTh = value.nameTh && value.nameTh.trim() !== '';
      const hasNameEn = value.nameEn && value.nameEn.trim() !== '';
      return hasNameTh && hasNameEn && checkMessageEn(value.nameEn || '');
    }
    return true;
  });

  return checkedValues.length > 0 && allOtherValid;
};
